import { runSdkRequest } from '~/features/module-runtime'
import { defModule, Task, type TaskMessage, type UpdateResult } from '@st/redux'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import type {
  BulkCreateChecklistItemsItem,
  Jurisdiction,
  STChecklistItem,
  STDocumentType
} from '@st/sdk'
import { deepEqual } from '@st/util/deep-equal'
import { SdkClient } from '~/effects'
import {
  initialChecklistDialogState,
  paymentInstructions,
  type PaymentInstructionsRequest,
  type ChecklistDraft,
  type ChecklistTarget,
  type ChecklistDialogRequest,
  type ChecklistDialog
} from './model'
import {
  generateTaxPaymentChecklistItemName,
  updatePayment,
  type PaymentMessage
} from './delivery/model'
import type {
  CreateChecklistItem,
  EditChecklistItem,
  SetChecklistItemAttachments,
  DeleteChecklistItem,
  CompleteChecklistItem,
  MarkChecklistItemIncomplete,
  SetChecklistItemIncompleteReasons,
  MoveChecklistItem,
  SetChecklistItemVisibility
} from '@st/sdk'
export type ChecklistRequest =
  | CreateChecklistItem
  | EditChecklistItem
  | SetChecklistItemAttachments
  | DeleteChecklistItem
  | CompleteChecklistItem
  | MarkChecklistItemIncomplete
  | SetChecklistItemIncompleteReasons
  | MoveChecklistItem
  | SetChecklistItemVisibility
export type ChecklistModel = {
  folderId: string
  year: number
  returnType: string
  dialog?: ChecklistDialog
  catalog?: Jurisdiction[]
}
const loadChecklistJurisdictions = Task.define(() =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const catalog = yield* sdk.send({ type: 'schema/jurisdictions' }).pipe(Effect.orDie)
    return { type: 'catalogLoaded', catalog } as const
  })
)

const loadPaymentInstructions = Task.define(
  (request: PaymentInstructionsRequest) =>
    Effect.gen(function* () {
      const sdk = yield* SdkClient
      const result = yield* runSdkRequest(sdk.send(request))
      if (!result.ok) return { type: 'instructionsFailed' } as const
      const guide = result.response.instructions
      if (!guide) return { type: 'instructionsLoaded', note: '' } as const
      return { type: 'instructionsLoaded', note: guide.note } as const
    }),
  { key: (request) => JSON.stringify(request) }
)

const previewDeliveryChecklist = Task.define((folderId: string) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* runSdkRequest(
      sdk.send({ type: 'folders/previewDeliveryChecklist', folderId })
    )
    return { type: 'generationPreviewLoaded', result } as const
  })
)
const createDeliveryChecklist = (folderId: string, items: BulkCreateChecklistItemsItem[]) =>
  Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* runSdkRequest(
      sdk.send({ type: 'folders/createChecklistItems', folderId, items, onDuplicate: 'skip' })
    )
    return { type: 'generationSaved', result } as const
  })

export type ChecklistMessage =
  | TaskMessage<typeof previewDeliveryChecklist>
  | Effect.Success<ReturnType<typeof createDeliveryChecklist>>
  | { type: 'previewDeliveryChecklist' }
  | { type: 'createDeliveryChecklist' }
  | TaskMessage<typeof loadChecklistJurisdictions>
  | TaskMessage<typeof loadPaymentInstructions>
  | { type: 'openChecklistDialog'; request: ChecklistDialogRequest }
  | { type: 'closeChecklistDialog' }
  | { type: 'checklistDraftChanged'; patch: Partial<ChecklistDraft> }
  | { type: 'incompleteReasonChanged'; value: string }
  | { type: 'completionToggled'; item: STChecklistItem }
  | { type: 'visibilityToggled'; item: STChecklistItem }
  | {
      type: 'checklistItemMoved'
      item: STChecklistItem
      referenceItem: STChecklistItem
      relation: MoveChecklistItem['relation']
    }
  | {
      type: 'documentRequestChanged'
      documentType: STDocumentType
    }
  | {
      type: 'taxPaymentChanged'
      message: PaymentMessage
    }
  | { type: 'checklistPickerToggled' }
  | { type: 'checklistPickerClosed' }
  | { type: 'checklistAttachmentPicked'; documentId: string }
  | { type: 'attachmentRemoved'; reference: STChecklistItem['attachments'][number] }
  | { type: 'saveChecklistDialog' }
  | { type: 'persist'; requests: ChecklistRequest[] }

type ChecklistUpdate = UpdateResult<ChecklistModel, ChecklistMessage>

export const checklistModule = defModule<{ state: ChecklistModel; message: ChecklistMessage; init: Omit<ChecklistModel, 'dialog' | 'catalog'> }>({
  init: (input) => input,
  // Load the jurisdiction catalog before instructions: their result updates the ready editor.
  tasks: (state) =>
    match(state.dialog)
      .with({ mode: 'generation', status: 'loading' }, () => [
        previewDeliveryChecklist(state.folderId)
      ])
      .with({ mode: 'loading' }, () => [loadChecklistJurisdictions()])
      .with(
        { mode: 'edit', editor: { kind: 'tax_payment', instructions: { status: 'loading' } } },
        ({ editor }) => [loadPaymentInstructions(editor.instructions.request)]
      )
      .otherwise(() => []),
  update: (state, message): ChecklistUpdate => {
    return match(message)
      .returnType<ChecklistUpdate>()
      .with({ type: 'previewDeliveryChecklist' }, () => {
        return { ...state, dialog: { mode: 'generation', status: 'loading', items: [] } }
      })
      .with({ type: 'generationPreviewLoaded' }, ({ result }) => {
        if (!result.ok)
          return {
            ...state,
            dialog: {
              mode: 'generation',
              status: 'failed',
              items: [],
              error: 'Could not load payments. Please try again.'
            }
          }
        return { ...state, dialog: { mode: 'generation', status: 'ready', items: result.response } }
      })
      .with({ type: 'createDeliveryChecklist' }, () => {
        if (state.dialog?.mode !== 'generation')
          throw new Error('Generation requested outside preview')
        return [
          { ...state, dialog: { ...state.dialog, status: 'saving', error: undefined } },
          createDeliveryChecklist(state.folderId, state.dialog.items)
        ]
      })
      .with({ type: 'generationSaved' }, ({ result }) => {
        if (state.dialog?.mode !== 'generation')
          throw new Error('Generation finished outside preview')
        if (!result.ok)
          return {
            ...state,
            dialog: {
              ...state.dialog,
              status: 'ready',
              error: 'Could not create checklist. Please try again.'
            }
          }
        return { ...state, dialog: undefined }
      })
      .with({ type: 'openChecklistDialog' }, ({ request }) => {
        return {
          ...state,
          dialog: initialChecklistDialogState(request, state.year, state.catalog, state.returnType)
        }
      })
      .with({ type: 'closeChecklistDialog' }, () => {
        if (state.dialog?.mode === 'generation' && state.dialog.status === 'saving') return state
        return { ...state, dialog: undefined }
      })
      .with({ type: 'catalogLoaded' }, (message) => {
        const dialog = match(state.dialog)
          .with({ mode: 'loading' }, (dialog) => dialog)
          .otherwise(() => {
            throw new Error('Catalog arrived outside payment loading')
          })
        return {
          ...state,
          catalog: message.catalog,
          dialog: { mode: 'edit', editor: { ...dialog.editor, catalog: message.catalog } }
        }
      })
      .with({ type: 'checklistDraftChanged' }, (message) => {
        const dialog = match(state.dialog)
          .with({ mode: 'edit' }, (dialog) => dialog)
          .otherwise(() => {
            throw new Error('Checklist draft changed outside editor')
          })
        return {
          ...state,
          dialog: {
            ...dialog,
            editor: match(dialog.editor)
              .with({ kind: 'document_request' }, (editor) => ({
                ...editor,
                draft: { ...editor.draft, ...message.patch }
              }))
              .with({ kind: 'questionnaire_entry' }, (editor) => ({
                ...editor,
                draft: { ...editor.draft, ...message.patch }
              }))
              .with({ kind: 'tax_payment' }, (editor) => ({
                ...editor,
                draft: { ...editor.draft, ...message.patch }
              }))
              .exhaustive()
          }
        }
      })
      .with({ type: 'incompleteReasonChanged' }, ({ value }) => {
        const dialog = match(state.dialog)
          .with(
            { mode: 'edit', editor: { kind: P.union('document_request', 'questionnaire_entry') } },
            (dialog) => dialog
          )
          .otherwise(() => {
            throw new Error('Reason change outside intake editor')
          })
        const editor = match(dialog.editor)
          .with({ kind: 'document_request' }, (editor) => ({
            ...editor,
            draft: { ...editor.draft, incompleteReason: value }
          }))
          .with({ kind: 'questionnaire_entry' }, (editor) => ({
            ...editor,
            draft: { ...editor.draft, incompleteReason: value }
          }))
          .exhaustive()
        return { ...state, dialog: { ...dialog, editor } }
      })
      .with({ type: 'completionToggled' }, ({ item }) => [
        state,
        persist([completionRequest(state.folderId, item)])
      ])
      .with({ type: 'visibilityToggled' }, ({ item }) => [
        state,
        persist([
          {
            type: 'folders/setChecklistItemVisibility',
            folderId: state.folderId,
            checklistItemId: item.id,
            visibility: match(item.visibility)
              .with(P.union(undefined, 'all'), () => 'organization' as const)
              .with('organization', () => 'all' as const)
              .exhaustive()
          }
        ])
      ])
      .with({ type: 'checklistItemMoved' }, ({ item, referenceItem, relation }) => [
        state,
        persist([
          {
            type: 'folders/moveChecklistItem',
            folderId: state.folderId,
            checklistItemId: item.id,
            referenceChecklistItemId: referenceItem.id,
            relation
          }
        ])
      ])
      .with({ type: 'documentRequestChanged' }, (message) => {
        const dialog = match(state.dialog)
          .with({ mode: 'edit', editor: { kind: 'document_request' } }, (dialog) => dialog)
          .otherwise(() => {
            throw new Error('Document change outside document editor')
          })
        return {
          ...state,
          dialog: {
            ...dialog,
            editor: {
              ...dialog.editor,
              draft: {
                ...dialog.editor.draft,
                name: message.documentType.name,
                context: {
                  type: 'document_request',
                  documentTypeId: message.documentType.id,
                  match: {}
                }
              }
            }
          }
        }
      })
      .with(P.union({ type: 'instructionsLoaded' }, { type: 'instructionsFailed' }), (message) => {
        const dialog = match(state.dialog)
          .with({ mode: 'edit', editor: { kind: 'tax_payment' } }, (dialog) => dialog)
          .otherwise(() => {
            throw new Error('Instructions arrived outside payment editor')
          })
        const editor = match(message)
          .with({ type: 'instructionsLoaded' }, ({ note }) => ({
            ...dialog.editor,
            draft: { ...dialog.editor.draft, note },
            instructions: undefined
          }))
          .with({ type: 'instructionsFailed' }, () => ({
            ...dialog.editor,
            instructions: { status: 'failed' as const }
          }))
          .exhaustive()
        return { ...state, dialog: { ...dialog, editor } }
      })
      .with({ type: 'taxPaymentChanged' }, (message) => {
        const dialog = match(state.dialog)
          .with({ mode: 'edit', editor: { kind: 'tax_payment' } }, (dialog) => dialog)
          .otherwise(() => {
            throw new Error('Payment change outside payment editor')
          })
        const context = updatePayment(
          dialog.editor.draft.context,
          message.message,
          dialog.editor.catalog,
          state.year
        )
        const instructions = paymentInstructions(context, state.returnType)
        const editor = {
          ...dialog.editor,
          draft: { ...dialog.editor.draft, context }
        }
        if (
          !deepEqual(
            paymentInstructions(dialog.editor.draft.context, state.returnType),
            instructions
          )
        ) {
          editor.draft.note = ''
          editor.instructions = instructions
        }
        return { ...state, dialog: { ...dialog, editor } }
      })
      .with(
        P.union({ type: 'checklistPickerToggled' },
        { type: 'checklistPickerClosed' },
        { type: 'checklistAttachmentPicked' },
        { type: 'attachmentRemoved' }),
        (message) => {
          const dialog = match(state.dialog)
            .with({ mode: P.union('attachments', 'complete') }, (dialog) => dialog)
            .otherwise(() => {
              throw new Error('Attachment change outside attachment dialog')
            })
          const next = match(message)
            .with({ type: 'checklistPickerToggled' }, () => ({
              ...dialog,
              isAttachmentPickerOpen: !dialog.isAttachmentPickerOpen
            }))
            .with({ type: 'checklistPickerClosed' }, () => ({
              ...dialog,
              isAttachmentPickerOpen: false
            }))
            .with({ type: 'checklistAttachmentPicked' }, ({ documentId }) => ({
              ...dialog,
              isAttachmentPickerOpen: false,
              attachments: [...dialog.attachments, { documentId }]
            }))
            .with({ type: 'attachmentRemoved' }, ({ reference }) => ({
              ...dialog,
              attachments: dialog.attachments.filter(
                (attachment) => !deepEqual(attachment, reference)
              )
            }))
            .exhaustive()
          return { ...state, dialog: next }
        }
      )
      .with({ type: 'saveChecklistDialog' }, () => {
        const dialog = state.dialog
        const closed = { ...state, dialog: undefined }
        return match(dialog)
          .returnType<ChecklistUpdate>()
          .with({ mode: 'edit' }, ({ editor }) => {
            const { target, draft } = editor
            const name = match(editor)
              .with({ kind: 'document_request' }, ({ draft }) => draft.name)
              .with({ kind: 'questionnaire_entry' }, ({ target }) => target.item.name)
              .with({ kind: 'tax_payment' }, ({ draft }) =>
                generateTaxPaymentChecklistItemName(draft.context)
              )
              .exhaustive()
            return [
              closed,
              persist(
                itemRequests(state.folderId, {
                  target,
                  fields: {
                    name,
                    note: draft.note,
                    dueOn: draft.dueOn || undefined,
                    context: draft.context
                  },
                  incompleteReason: match(editor)
                    .with({ kind: 'tax_payment' }, () => undefined)
                    .with(
                      { kind: P.union('document_request', 'questionnaire_entry') },
                      ({ draft }) => draft.incompleteReason
                    )
                    .exhaustive()
                })
              )
            ]
          })
          .with(P.union({ mode: 'attachments' }, { mode: 'complete' }), ({ item, attachments, mode }) => [
            closed,
            persist(
              attachmentRequests(state.folderId, {
                item,
                attachments,
                complete: mode == 'complete'
              })
            )
          ])
          .with({ mode: 'delete' }, ({ item }) => [
            closed,
            persist([
              {
                type: 'folders/deleteChecklistItem',
                folderId: state.folderId,
                checklistItemId: item.id
              }
            ])
          ])
          .with({ mode: 'manageCompletion' }, () => closed)
          .with(
            P.union(undefined,
            { mode: 'loading' },
            { mode: 'generation' }),
            () => {
              throw new Error('Save before editor loaded')
            }
          )
          .exhaustive()
      })
      .with({ type: 'persist' }, () => {
        throw new Error('Persistence is handled by the folder host')
      })
      .exhaustive()
  }
})

function itemRequests(
  folderId: string,
  message: {
    target: ChecklistTarget
    fields: Pick<STChecklistItem, 'name' | 'note' | 'dueOn' | 'context'>
    incompleteReason?: string
  }
): ChecklistRequest[] {
  const { target, fields } = message
  const requests: ChecklistRequest[] = match(target)
    .with({ mode: 'create' }, ({ phase }) => [
      {
        type: 'folders/createChecklistItem' as const,
        folderId,
        phase,
        visibility: 'all' as const,
        ...fields
      }
    ])
    .with({ mode: 'edit' }, ({ item }) => {
      const targetFields = (['name', 'note', 'context', 'dueOn'] as const)
        .filter((field) => !deepEqual(editFieldValue(fields, field), editFieldValue(item, field)))
        .map((field) =>
          match(field)
            .with('dueOn', () => 'due_on' as const)
            .otherwise((field) => field)
        )
      const edits: ChecklistRequest[] = []
      if (targetFields.length > 0)
        edits.push({
          type: 'folders/editChecklistItem',
          folderId,
          checklistItemId: item.id,
          targetFields,
          ...fields
        })
      if (
        message.incompleteReason !== undefined &&
        message.incompleteReason != (item.incompleteReason ?? '')
      )
        edits.push({
          type: 'folders/setChecklistItemIncompleteReasons',
          folderId,
          options: [{ checklistItemId: item.id, incompleteReason: message.incompleteReason }]
        })
      return edits
    })
    .exhaustive()
  return requests
}

function attachmentRequests(
  folderId: string,
  message: { item: STChecklistItem; attachments: STChecklistItem['attachments']; complete: boolean }
): ChecklistRequest[] {
  const { item, attachments } = message
  const requests: ChecklistRequest[] = []
  if (!deepEqual(item.attachments, attachments))
    requests.push({
      type: 'folders/setChecklistItemAttachments',
      folderId,
      checklistItemId: item.id,
      attachments
    })
  if (message.complete) requests.push(completionRequest(folderId, item))
  return requests
}

function persist(requests: ChecklistRequest[]) {
  return Effect.succeed({ type: 'persist' as const, requests })
}

function completionRequest(
  folderId: string,
  item: STChecklistItem
): CompleteChecklistItem | MarkChecklistItemIncomplete {
  return {
    type: match(item.status)
      .with('complete', () => 'folders/markChecklistItemIncomplete' as const)
      .with('incomplete', () => 'folders/completeChecklistItem' as const)
      .exhaustive(),
    folderId,
    checklistItemId: item.id
  }
}

function editFieldValue(
  item: Pick<STChecklistItem, 'name' | 'note' | 'context' | 'dueOn'>,
  field: 'name' | 'note' | 'context' | 'dueOn'
) {
  // The editor represents a blank note as an empty string, while stored notes may be absent.
  return match(field)
    .with('note', () => item.note ?? '')
    .otherwise(() => item[field] ?? null)
}
